import { buildDcCircuitTopology, dcInputPorts } from "./dc-circuit-topology";
import { isAcTerminalId } from "./ac";
import { effectiveTerminals } from "./effective";
import { isSenseTerminal } from "./sense-terminal";
import { getDefinition, type DefLookup } from "./catalog";
import { dcPortTerminalGroups, isPvTerminalId } from "./component-ports";
import {
  buildGraph,
  type Vertex,
  type Edge,
} from "./diagnostics/graph";
import { terminalPolarity } from "./terminal-snap";
import type { Cable, System, SystemNode } from "./types";

const drawnReturnCache = new WeakMap<
  Record<string, SystemNode>, WeakMap<Cable[], Map<DefLookup, { drawn: Set<string>; budgeted: Set<string> }>>
>();

/** `voltageDrop` assumes a mirrored return. Where a complete circuit is drawn,
 * report each physical conductor once; cumulative diagnostics sum both legs.
 * The cache includes nodes, cables and lookup so changing only a component
 * cannot reuse an obsolete topology. */
export function cableVoltageDropWeight(
  cableId: string,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
  defLookup: DefLookup,
): number {
  return returnPaths(nodesById, cables, defLookup).drawn.has(cableId) ? 0.5 : 1;
}

/** Only load paths have a cumulative auto-sizing budget. Other circuits keep
 * their mirrored-return sizing minimum even when the display can report each
 * physical conductor once. Reporting I×R must not halve a charger's wire or
 * assign the whole starter-loop allowance independently to each conductor. */
export function cableSizingDropWeight(
  cableId: string,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
  defLookup: DefLookup,
): number {
  return returnPaths(nodesById, cables, defLookup).budgeted.has(cableId) ? 0.5 : 1;
}

function returnPaths(nodesById: Record<string, SystemNode>, cables: Cable[], defLookup: DefLookup) {
  let byCables = drawnReturnCache.get(nodesById);
  if (!byCables) {
    byCables = new WeakMap();
    drawnReturnCache.set(nodesById, byCables);
  }
  let byLookup = byCables.get(cables);
  if (!byLookup) {
    byLookup = new Map();
    byCables.set(cables, byLookup);
  }
  let drawn = byLookup.get(defLookup);
  if (!drawn) {
    const system: System = { version: 0, name: "", nodes: Object.values(nodesById), cables, zoneDistances: {} };
    drawn = {
      drawn: completeCircuitCables(system, defLookup),
      budgeted: new Set(collectLoadCircuits(system, defLookup).filter(c => c.complete).flatMap(c => [...c.pos, ...c.neg])),
    };
    byLookup.set(defLookup, drawn);
  }
  return drawn;
}

/** Find all physical conductors in a complete drawn circuit, including every
 * parallel route. A nearest-battery BFS selects only one route and misses PV,
 * charger outputs and isolated converter loads entirely.
 *
 * Device port edges join each DC side's positive and negative solely for this
 * continuity test, never for voltage/current propagation. A cyclic block with
 * such an edge has a drawn out-and-back path. Pure parallel positive wires do
 * not qualify: their cycle contains no device port and may lack any return. */
function completeCircuitCables(system: System, defLookup: DefLookup): Set<string> {
  type CircuitEdge = { id: number; a: Vertex; b: Vertex; cableId?: string; port?: true };
  const adjacency = new Map<Vertex, CircuitEdge[]>();
  let nextId = 0;
  const add = (a: Vertex, b: Vertex, extra: Pick<CircuitEdge, "cableId" | "port"> = {}) => {
    const edge: CircuitEdge = { id: nextId++, a, b, ...extra };
    (adjacency.get(a) ?? adjacency.set(a, []).get(a)!).push(edge);
    (adjacency.get(b) ?? adjacency.set(b, []).get(b)!).push(edge);
  };
  const graph = buildGraph(system, defLookup, { excludeSense: true });
  const seen = new Set<Edge>();
  for (const [from, neighbours] of graph.adj) {
    for (const { vertex, via } of neighbours) {
      if (seen.has(via)) continue;
      seen.add(via);
      add(from, vertex, via.kind === "cable" ? { cableId: via.cableId } : {});
    }
  }
  const wired = new Set(system.cables.flatMap(c => [
    `${c.fromNodeId}:${c.fromTerminalId}`, `${c.toNodeId}:${c.toTerminalId}`,
  ]));
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    // A powered monitor has a real consuming DC pair, unlike a passive shunt
    // or its voltage-sense lead. Only display loss gets the physical one-way
    // weight; the existing conservative sizing floor remains unchanged.
    const poweredMonitor = def.category === "monitor" &&
      [def.ampDraw, def.ampSurge].some(amps => amps != null && Number.isFinite(amps) && amps > 0);
    if (!poweredMonitor && !["battery", "solar", "source", "engine", "load", "charger"].includes(def.category)) continue;
    const ports = poweredMonitor
      ? dcPortTerminalGroups({ ...def, terminals: effectiveTerminals(def, node).filter(t => !isSenseTerminal(def, t)) }, "input")
      : def.category === "charger"
      ? [
          ...dcPortTerminalGroups(def, "input"),
          ...dcPortTerminalGroups(def, "output"),
          // A dual-input charger has a separate PV port alongside its DC one.
          ...dcPortTerminalGroups({ ...def, terminals: def.terminals.filter(t => isPvTerminalId(t.id)) }, "input"),
        ]
      : def.category === "load"
        ? [...dcPortTerminalGroups(def, "input"), ...dcPortTerminalGroups(def, "output")]
        : [def.terminals];
    for (const port of ports) {
      const terminals = port.filter(t => !isAcTerminalId(t.id) && wired.has(`${node.id}:${t.id}`));
      const positive = terminals.filter(t => terminalPolarity(t) === "positive");
      const negative = terminals.filter(t => terminalPolarity(t) === "negative");
      for (const p of positive) for (const n of negative) add(`${node.id}:${p.id}`, `${node.id}:${n.id}`, { port: true });
    }
  }

  // Tarjan's edge-stack decomposition keeps alternative paths together while
  // separating dangling wires and cycles attached at only one junction.
  const entered = new Map<Vertex, number>();
  const lowest = new Map<Vertex, number>();
  const stack: CircuitEdge[] = [];
  const drawn = new Set<string>();
  let clock = 0;
  const visit = (vertex: Vertex, parentEdge?: number) => {
    entered.set(vertex, ++clock);
    lowest.set(vertex, clock);
    for (const edge of adjacency.get(vertex) ?? []) {
      if (edge.id === parentEdge) continue;
      const other = edge.a === vertex ? edge.b : edge.a;
      if (!entered.has(other)) {
        stack.push(edge);
        visit(other, edge.id);
        lowest.set(vertex, Math.min(lowest.get(vertex)!, lowest.get(other)!));
        if (lowest.get(other)! >= entered.get(vertex)!) {
          const block: CircuitEdge[] = [];
          let popped: CircuitEdge;
          do { popped = stack.pop()!; block.push(popped); } while (popped.id !== edge.id);
          if (block.length > 1 && block.some(e => e.port)) {
            for (const e of block) if (e.cableId) drawn.add(e.cableId);
          }
        }
      } else if (entered.get(other)! < entered.get(vertex)!) {
        stack.push(edge);
        lowest.set(vertex, Math.min(lowest.get(vertex)!, entered.get(other)!));
      }
    }
  };
  for (const vertex of adjacency.keys()) if (!entered.has(vertex)) visit(vertex);
  return drawn;
}

/**
 * A complete physical source-paired loop, or an explicitly incomplete
 * positive-only estimate. Both cable lists start at the load; series source
 * links follow the positive list. `complete` distinguishes an omitted return
 * from a real zero-cable internal return and prevents unrelated negative rails
 * from receiving a complete-loop voltage-drop budget.
 */
export type LoadCircuit = {
  loadVertex: Vertex;
  pos: string[];
  neg: string[];
  complete: boolean;
  sourceId?: string;
};

/** Keyed on `System` identity: the store hands out a fresh object per mutation. */
const circuitCache = new WeakMap<System, WeakMap<DefLookup, LoadCircuit[]>>();

export function collectLoadCircuits(
  system: System,
  defLookup: DefLookup = getDefinition,
): LoadCircuit[] {
  let byLookup = circuitCache.get(system);
  if (!byLookup) { byLookup = new WeakMap(); circuitCache.set(system, byLookup); }
  const cached = byLookup.get(defLookup);
  if (cached) return cached;
  const result = collectLoadCircuitsImpl(system, defLookup);
  byLookup.set(defLookup, result);
  return result;
}

function collectLoadCircuitsImpl(system: System, defLookup: DefLookup): LoadCircuit[] {
  if (system.cables.length === 0) return [];
  const topology = buildDcCircuitTopology(system, defLookup);
  const nodes = new Map(system.nodes.map(node => [node.id, node]));
  const out: LoadCircuit[] = [];
  for (const port of dcInputPorts(system, defLookup)) {
    const node = nodes.get(port.nodeId);
    if (node?.kind !== "component" || defLookup(node.definitionId, node.customDefinition)?.category !== "load") continue;
    const resolution = topology.resolvePort(port);
    // Prefer a connected battery over a charger on the same bus. A converter
    // output remains a separate source pair on batteryless downstream rails.
    const batteryRoutes = resolution.routes.filter(route => route.source.kind === "battery");
    const routes = batteryRoutes.length ? batteryRoutes : resolution.routes;
    for (const route of routes) {
      const pos = [...route.positive].reverse().map(step => step.cableId)
        .concat([...route.series].reverse().map(step => step.cableId));
      if (!pos.length) continue;
      out.push({
        loadVertex: route.positiveTerminal, pos,
        neg: route.negative.map(step => step.cableId),
        complete: true, sourceId: route.sourceId,
      });
    }
    if (routes.length) continue;
    // A positive-only drawing may still receive a conservative mirrored-return
    // sizing estimate. This is an explicit incomplete policy, never evidence
    // that some other battery's negative completes the circuit.
    const incomplete = resolution.incomplete.filter(route => route.positive.length > 0 && route.reason !== "shorted-rails")
      .sort((a, b) => a.positive.length - b.positive.length || a.sourceId.localeCompare(b.sourceId))[0];
    if (incomplete) out.push({
      loadVertex: incomplete.positiveTerminal,
      pos: [...incomplete.positive].reverse().map(step => step.cableId), neg: [],
      complete: false, sourceId: incomplete.sourceId,
    });
  }
  return out;
}
